import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import { createPortal } from "react-dom";
import classes from "./AvailabilityModal.module.css";
import ErrorMessage from "../StudioErstellen/ErrorMessage";
import { saveAvailabilityReplacement } from "../../util/availabilityPersistence";
import { isBusyBookingStatus } from "../../util/studioAvailability";

const DayComponent = ({
    dayName,
    dayData,
    onDayDataChange,
    minNumberOfHours,
    bookings,
    dayKey,
    allDaysData,
    onCopyToOtherDays
}) => {
    const isIOSSafari =
        typeof window !== "undefined" &&
        /iP(ad|hone|od)/.test(window.navigator.userAgent) &&
        /Safari/.test(window.navigator.userAgent) &&
        !/CriOS|FxiOS|EdgiOS/.test(window.navigator.userAgent);

    const [isActive, setIsActive] = useState(dayData.isActive);
    const [is24Hours, setIs24Hours] = useState(dayData.is24Hours);
    const [timeSlots, setTimeSlots] = useState(dayData.timeSlots);
    const [error, setError] = useState(null);
    const [showCopyIcon, setShowCopyIcon] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedDays, setSelectedDays] = useState([]);
    const [hasUserChangedTimes, setHasUserChangedTimes] = useState(false);

    // Check if time slots are default (07:00-21:00)
    const isDefaultTime = (slots) => {
        if (!slots || slots.length !== 1) return false;
        return slots[0].start === '07:00' && slots[0].end === '21:00';
    };

    // Get initial time slots from dayData
    const getInitialTimeSlots = () => {
        if (dayData.timeSlots && dayData.timeSlots.length > 0 && dayData.timeSlots[0].start && dayData.timeSlots[0].end) {
            return dayData.timeSlots;
        }
        return [{ start: '07:00', end: '21:00' }];
    };

    // Initialize hasUserChangedTimes based on whether slots are default
    useEffect(() => {
        setHasUserChangedTimes(!isDefaultTime(timeSlots));
    }, []);

    // Sync internal state when dayData prop changes (e.g., after copying from another day)
    useEffect(() => {
        setIsActive(dayData.isActive ?? false);
        setIs24Hours(dayData.is24Hours ?? false);
        const newTimeSlots = getInitialTimeSlots();
        setTimeSlots(newTimeSlots);
        // If times are different from default, mark as user-changed
        if (!isDefaultTime(newTimeSlots)) {
            setHasUserChangedTimes(true);
        } else {
            setHasUserChangedTimes(false);
        }
    }, [dayData]);

    // Show copy icon when user has changed times and day is active with specific hours
    useEffect(() => {
        if (isActive && !is24Hours) {
            setShowCopyIcon(hasUserChangedTimes && !isDefaultTime(timeSlots));
        } else {
            setShowCopyIcon(false);
        }
    }, [timeSlots, isActive, is24Hours, hasUserChangedTimes]);

    useEffect(() => {
        if (!showModal) return;

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = originalOverflow || '';
        };
    }, [showModal]);

    const updateParent = (newIsActive, newIs24Hours, newTimeSlots) => {
        onDayDataChange({
            isActive: newIsActive,
            is24Hours: newIs24Hours,
            timeSlots: newTimeSlots
        });
    };

    const handleActiveChange = (newIsActive) => {
        setError(null);

        // If turning off a day with bookings, we should prevent it
        if (!newIsActive && bookings && bookings.length > 0) {
            setError(`Es gibt bereits Buchungen an diesem Tag. Bitte stornieren Sie diese Buchungen zuerst.`);
            return; // Don't allow turning off a day with bookings
        }

        // Allow the user to change the active state freely if no bookings
        setIsActive(newIsActive);
        updateParent(newIsActive, is24Hours, timeSlots);
    };

    const handle24HoursChange = (newIs24Hours) => {
        setError(null);

        // Allow the user to change the 24 hours state freely
        setIs24Hours(newIs24Hours);

        // If switching to 24 hours, adjust the timeSlots accordingly
        const updatedTimeSlots = newIs24Hours
            ? [{ start: "00:00", end: "23:59" }]
            : [{ start: '', end: '' }];

        setTimeSlots(updatedTimeSlots);

        // When switching to 24 hours, we should never have conflicts because all times are covered
        if (newIs24Hours) {
            updateParent(isActive, newIs24Hours, updatedTimeSlots);
            return;
        }

        // Only check conflicts when switching from 24 hours to specific hours
        if (bookings && bookings.length > 0) {
            // Empty slots won't cover any booking
            const conflicts = checkBookingsNotCovered(updatedTimeSlots);
            if (conflicts) {
                setError(`Es gibt bereits Buchungen in diesem Zeitraum.`);
                // Don't prevent the switch, just show the error
            }
        }

        updateParent(isActive, newIs24Hours, updatedTimeSlots);
    };

    const generateHours = () => {
        const hours = [];
        for (let i = 0; i <= 24; i++) {
            hours.push(i.toString().padStart(2, '0') + ':00');
        }
        return hours;
    };

    const getAvailableStartTimes = (slotIndex) => {
        const hours = generateHours();
        if (slotIndex === 0) {
            // If minNumberOfHours is set, filter out start times that would exceed midnight
            if (minNumberOfHours) {
                return hours.filter(time => {
                    const hour = parseInt(time.split(':')[0]);
                    return hour <= (24 - minNumberOfHours);
                });
            }
            return hours;
        }

        const previousSlot = timeSlots[slotIndex - 1];
        if (!previousSlot.end) return hours;

        const previousEndHour = parseInt(previousSlot.end.split(':')[0]);

        // Apply both filters
        return hours.filter(time => {
            const hour = parseInt(time.split(':')[0]);
            const isAfterPreviousSlot = hour >= previousEndHour;
            const hasEnoughTimeBeforeMidnight = !minNumberOfHours || hour <= (24 - minNumberOfHours);
            return isAfterPreviousSlot && hasEnoughTimeBeforeMidnight;
        });
    };

    const getAvailableEndTimes = (startTime, slotIndex) => {
        if (!startTime) return [];
        const hours = generateHours();
        const startHour = parseInt(startTime.split(':')[0]);

        // Apply minNumberOfHours constraint
        if (minNumberOfHours) {
            const minEndHour = startHour + 1;

            const maxStartHour = 24 - minNumberOfHours;

            if (minEndHour > maxStartHour) {

                const maxEndHour = maxStartHour + minNumberOfHours;

                return hours.filter(time => parseInt(time.split(':')[0]) >= maxEndHour);
            }

            return hours.filter(time => parseInt(time.split(':')[0]) >= minEndHour);
        }

        return hours.filter(time => parseInt(time.split(':')[0]) > startHour);
    };

    const canAddNewSlot = () => {
        const lastSlot = timeSlots[timeSlots.length - 1];
        return lastSlot.end && parseInt(lastSlot.end.split(':')[0]) < 24;
    };

    const handleTimeChange = (index, field, value) => {
        setError(null);
        const newTimeSlots = [...timeSlots];
        newTimeSlots[index] = { ...newTimeSlots[index], [field]: value };

        if (field === 'start') {
            const startHour = parseInt(value.split(':')[0]);

            // Check if this start time would exceed available hours with minNumberOfHours
            if (minNumberOfHours && (startHour + minNumberOfHours > 24)) {
                setError(`Zeitslot muss mindestens ${minNumberOfHours} Stunden betragen. Bitte w�hlen Sie eine fr�here Startzeit.`);
                // Set end time to 24:00 as fallback
                newTimeSlots[index].end = '24:00';
            } else {
                // Set end time to be at least minNumberOfHours later (if applicable)
                const availableEndTimes = getAvailableEndTimes(value, index);
                newTimeSlots[index].end = availableEndTimes[0] || '';
            }
        }

        // Check for conflict with bookings
        if (bookings && bookings.length > 0) {
            const conflicts = checkBookingConflicts(newTimeSlots);
            if (conflicts) {
                setError(`Es gibt bereits Buchungen in diesem Zeitraum.`);
            }
        }

        setTimeSlots(newTimeSlots);
        setHasUserChangedTimes(true); // Mark that user has changed times
        updateParent(isActive, is24Hours, newTimeSlots);
    };

    const checkBookingConflicts = (slots) => {
        if (!bookings || !bookings.length) {
            return false;
        }

        // If we're checking a 24-hour slot, it should never conflict
        const is24HourSlot = slots.length === 1 &&
                             slots[0].start === "00:00" &&
                             (slots[0].end === "23:59" || slots[0].end === "24:00");

        if (is24HourSlot) {
            return false;
        }

        return checkBookingsNotCovered(slots);
    };

    const addNewTimeSlot = () => {
        const newTimeSlots = [...timeSlots, { start: '', end: '' }];
        setTimeSlots(newTimeSlots);
        setHasUserChangedTimes(true); // Mark that user has changed times
        updateParent(isActive, is24Hours, newTimeSlots);
    };

    const deleteTimeSlot = (index) => {
        setError(null);
        const newTimeSlots = timeSlots.filter((_, i) => i !== index);

        // Important: If there are no time slots left, add an empty one
        if (newTimeSlots.length === 0) {
            newTimeSlots.push({ start: '', end: '' });
        }

        // Check for conflicts after deletion
        if (bookings && bookings.length > 0) {
            const conflicts = checkBookingsNotCovered(newTimeSlots);

            if (conflicts) {
                setError(`Es gibt bereits Buchungen in diesem Zeitraum.`);
                return;
            }
        }

        setTimeSlots(newTimeSlots);
        setHasUserChangedTimes(true); // Mark that user has changed times
        updateParent(isActive, is24Hours, newTimeSlots);
    };

    // Copy functionality
    const compareTimeSlots = (slots1, slots2) => {
        if (!slots1 || !slots2 || slots1.length !== slots2.length) return false;

        const normalizeSlots = (slots) => {
            return slots
                .filter(slot => slot.start && slot.end)
                .map(slot => `${slot.start}-${slot.end}`)
                .sort()
                .join(',');
        };

        return normalizeSlots(slots1) === normalizeSlots(slots2);
    };

    const hasSameTimeSlots = (day) => {
        if (day === dayKey) return true;
        const targetDayData = allDaysData[day];
        if (!targetDayData) return false;

        // If target day has 24 hours enabled, it's not the same (allow copying to it)
        if (targetDayData.is24Hours) return false;

        return compareTimeSlots(timeSlots, targetDayData.timeSlots) &&
               isActive === targetDayData.isActive &&
               is24Hours === targetDayData.is24Hours;
    };

    const handleCopyClick = () => {
        setShowModal(true);
        setSelectedDays([]);
    };

    const handleDayToggle = (day) => {
        // Don't allow toggling if day has same time slots or is the source day
        if (day === dayKey || hasSameTimeSlots(day)) return;

        setSelectedDays(prev =>
            prev.includes(day)
                ? prev.filter(d => d !== day)
                : [...prev, day]
        );
    };

    const handleCopyTimes = () => {
        if (selectedDays.length === 0) return;
        // When copying time slots, always set is24Hours to false
        onCopyToOtherDays(dayKey, selectedDays, {
            isActive: isActive,
            is24Hours: false, // Always disable 24 hours when copying time slots
            timeSlots: timeSlots
        });
        setShowModal(false);
        setSelectedDays([]);
    };

    const dayNamesMap = {
        monday: 'Montag',
        tuesday: 'Dienstag',
        wednesday: 'Mittwoch',
        thursday: 'Donnerstag',
        friday: 'Freitag',
        saturday: 'Samstag',
        sunday: 'Sonntag'
    };

    const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

    // Fix 1: In the DayComponent's checkBookingsNotCovered function
    const checkBookingsNotCovered = (slots) => {
        if (!bookings || !bookings.length) {
            return false;
        }
        // Any empty slots can't cover bookings
        const validSlots = slots.filter(slot => slot.start && slot.end);

        // If we have no valid slots and have bookings, we have a conflict
        if (validSlots.length === 0 && bookings.length > 0) {
            return true;
        }

        // First, detect contiguous slots covering the full day
        if (validSlots.length > 1) {
            // Sort slots by start time
            const sortedSlots = [...validSlots].sort((a, b) => {
                return parseInt(a.start.split(':')[0]) - parseInt(b.start.split(':')[0]);
            });

            // Check if slots are contiguous
            let isContiguous = true;
            for (let i = 0; i < sortedSlots.length - 1; i++) {
                const currentEnd = parseInt(sortedSlots[i].end.split(':')[0]);
                const nextStart = parseInt(sortedSlots[i + 1].start.split(':')[0]);

                if (currentEnd !== nextStart) {
                    isContiguous = false;
                    break;
                }
            }

            // If slots are contiguous, treat them as a single range
            if (isContiguous) {
                const combinedSlot = {
                    start: sortedSlots[0].start,
                    end: sortedSlots[sortedSlots.length - 1].end
                };

                // For each booking, check if the combined range covers it
                return bookings.some(booking => {
                    const bookingStart = parseInt(booking.startTime.split(':')[0]);
                    const bookingEnd = parseInt(booking.endTime.split(':')[0]);

                    const slotStart = parseInt(combinedSlot.start.split(':')[0]);
                    const slotEnd = parseInt(combinedSlot.end.split(':')[0]);

                    const isCovered = slotStart <= bookingStart && slotEnd >= bookingEnd;

                    return !isCovered;
                });
            }
        }

        // If slots aren't contiguous or there's just one slot, check each booking
        const hasUncoveredBookings = bookings.some(booking => {
            const bookingStart = parseInt(booking.startTime.split(':')[0]);
            const bookingEnd = parseInt(booking.endTime.split(':')[0]);

            // Check if ANY slot fully covers this booking
            const isBookingCovered = validSlots.some(slot => {
                const slotStart = parseInt(slot.start.split(':')[0]);
                const slotEnd = parseInt(slot.end.split(':')[0]);


                // A booking is covered if the slot completely contains it
                const isCovered = slotStart <= bookingStart && slotEnd >= bookingEnd;
                return isCovered;
            });

            // If the booking is NOT covered by any slot, that's a conflict
            return !isBookingCovered;
        });

        return hasUncoveredBookings;
    };

    return (
        <div className={classes.dayContainer}>
            <div className={classes.dayContainerInner}>
                <div className={classes.day}>
                    <div className={classes.switchContainer}>
                        <label className={classes.switch}>
                            <input
                                type="checkbox"
                                checked={isActive}
                                onChange={(e) => handleActiveChange(e.target.checked)}
                            />
                            <span className={classes.slider}></span>
                        </label>
                    </div>
                    <span className="titleSemibold3" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start' }}>
                        {dayName}
                        {/* {bookings && bookings.length > 0 && (
                            <div className={classes.bookingIndicatorContainer}>
                                <span className={`${classes.bookingIndicator} semiboldXs`} title={`${bookings.length} Buchungen an diesem Tag`}>
                                    {bookings.length}
                                </span>
                            </div>
                        )} */}
                    </span>
                </div>
                {isActive && (
                    <div className={classes.periodContainer}>
                        <div
                            className={`${classes.period} ${is24Hours ? classes.selected : ''}`}
                            onClick={() => handle24HoursChange(true)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                                <path d="M14 7.56774C14 11.11 11.0951 13.9909 7.5 13.9909C3.90493 13.9909 1 11.11 1 7.56774C1 4.02548 3.90493 1.14453 7.5 1.14453C11.0951 1.14453 14 4.02548 14 7.56774Z" stroke="#1F1F1F"/>
                                <ellipse cx="7.5" cy="7.56782" rx="4.5" ry="4.45063" fill="#1F1F1F"/>
                            </svg>
                            <span className="semiboldMiddle">24 Stunden</span>
                        </div>
                        <div
                            className={`${classes.period} ${!is24Hours ? classes.selected : ''}`}
                            onClick={() => handle24HoursChange(false)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                                <path d="M14 7.56774C14 11.11 11.0951 13.9909 7.5 13.9909C3.90493 13.9909 1 11.11 1 7.56774C1 4.02548 3.90493 1.14453 7.5 1.14453C11.0951 1.14453 14 4.02548 14 7.56774Z" stroke="#1F1F1F"/>
                                <ellipse cx="7.5" cy="7.56782" rx="4.5" ry="4.45063" fill="#1F1F1F"/>
                            </svg>
                            <span className="semiboldMiddle">Stunden auswählen</span>
                        </div>
                    </div>
                )}
            </div>
            {isActive && !is24Hours && (
                <div className={classes.timesAndCopyContainer}>
                    <div className={classes.timesContainer}>
                        {timeSlots.map((slot, index) => (
                            <div key={index} className={classes.timeInputContainer}>
                                <div className={classes.timeSelect}>
                                    <div className={classes.selectWrapper}>
                                        <select
                                            value={slot.start}
                                            onChange={(e) => handleTimeChange(index, 'start', e.target.value)}
                                            className="semiboldMiddle"
                                        >
                                            <option value="">Start</option>
                                            {getAvailableStartTimes(index).map((time) => (
                                                <option key={time} value={time}>
                                                    {time}
                                                </option>
                                            ))}
                                        </select>
                                        <div className={classes.selectContent}>
                                            <span className={`semiboldMiddle ${isDefaultTime(timeSlots) && slot.start === '07:00' ? classes.defaultTime : ''}`}>{slot.start || 'Start'}</span>
                                            <svg className={classes.selectArrow} xmlns="http://www.w3.org/2000/svg" width="14" height="7" viewBox="0 0 14 7" fill="none">
                                                <path d="M6.87521 7L6.50678 6.63158L0.888364 1.01316L1.90152 1.35096e-07L6.87521 4.97368L12.2173 1.20818e-08L13.2305 1.10526L6.87521 7Z" fill="#1F1F1F"/>
                                            </svg>
                                        </div>
                                    </div>
                                    <span className={classes.dash}>-</span>
                                    <span className={`semiboldMiddle ${classes.smallerSpan}`}>Ende</span>
                                    <div className={classes.selectWrapper}>
                                        <select
                                            value={slot.end}
                                            onChange={(e) => handleTimeChange(index, 'end', e.target.value)}
                                            disabled={!slot.start}
                                            className="semiboldMiddle"
                                        >
                                            <option value="">Ende</option>
                                            {getAvailableEndTimes(slot.start, index).map((time) => (
                                                <option key={time} value={time}>
                                                    {time}
                                                </option>
                                            ))}
                                        </select>
                                        <div className={classes.selectContent}>
                                            <span className={`semiboldMiddle ${isDefaultTime(timeSlots) && slot.end === '21:00' ? classes.defaultTime : ''}`}>{slot.end || 'Ende'}</span>
                                            <svg className={classes.selectArrow} xmlns="http://www.w3.org/2000/svg" width="14" height="7" viewBox="0 0 14 7" fill="none">
                                                <path d="M6.87521 7L6.50678 6.63158L0.888364 1.01316L1.90152 1.35096e-07L6.87521 4.97368L12.2173 1.20818e-08L13.2305 1.10526L6.87521 7Z" fill="#1F1F1F"/>
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                                {index !== 0 && (
                                    <button
                                        className={classes.deleteButton}
                                        onClick={() => deleteTimeSlot(index)}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                                            <path fillRule="evenodd" clipRule="evenodd" d="M5.8008 6.33789L0.5 1.03709L1.1992 0.337891L6.5 5.63869L11.8008 0.337891L12.5 1.03709L7.1992 6.33789L12.5 11.6387L11.8008 12.3379L6.5 7.03709L1.1992 12.3379L0.5 11.6387L5.8008 6.33789Z" fill="#1F1F1F"/>
                                        </svg>
                                    </button>
                                )}
                            </div>
                        ))}
                        {error &&
                        <ErrorMessage >{error}</ErrorMessage>
                        }
                        <button
                            className={`${classes.addSlot} semiboldXs`}
                            onClick={addNewTimeSlot}
                            disabled={!canAddNewSlot()}
                            style={{ cursor: !canAddNewSlot() ? 'context-menu' : 'pointer' }}
                        >
                            Zeitslot hinzufügen
                        </button>
                    </div>
                    {showCopyIcon && (
                        <div onClick={handleCopyClick} className={classes.copyContainer}>
                            <div
                                className={classes.copyIconWrapper}
                                onMouseEnter={() => setShowTooltip(true)}
                                onMouseLeave={() => setShowTooltip(false)}
                            >
                                {showTooltip && (
                                    <div className={classes.tooltip}>
                                        Zeiten für andere Tage übernehmen
                                        <div className={classes.tooltipArrow}></div>
                                    </div>
                                )}

                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="24" viewBox="0 0 20 24" fill="none">
                                    <path d="M13 21.8555V23.3555H3V21.8555H13ZM14.5 20.3555V6.35547C14.5 5.52704 13.8284 4.85547 13 4.85547H3C2.17157 4.85547 1.5 5.52704 1.5 6.35547V20.3555C1.5 21.1839 2.17157 21.8555 3 21.8555V23.3555L2.8457 23.3516C1.31166 23.2738 0.0816253 22.0438 0.00390625 20.5098L0 20.3555V6.35547C0 4.69861 1.34315 3.35547 3 3.35547H13C14.6569 3.35547 16 4.69861 16 6.35547V20.3555C16 22.0123 14.6569 23.3555 13 23.3555V21.8555C13.8284 21.8555 14.5 21.1839 14.5 20.3555Z" fill="#1F1F1F"/>
                                    <path d="M17.8213 3.75C17.8213 2.50736 16.8139 1.5 15.5713 1.5H3.42773V0H15.5713C17.6424 0 19.3213 1.67893 19.3213 3.75V19.9609H17.8213V3.75Z" fill="#1F1F1F"/>
                                </svg>

                            </div>
                        </div>
                    )}
                </div>
            )}
        {showModal && typeof document !== "undefined" && createPortal(
            <>
                <div className={classes.copyTimesOverlay} onClick={() => setShowModal(false)}></div>
                <div
                    className={`${classes.copyTimesPopup} ${isIOSSafari ? classes.copyTimesPopupIOS : ''}`}
                    onClick={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                >
                    <div className={classes.modalContent}>
                        <header className={classes.modalHeader}>
                            <h2 className="mediumLarge">Zeiten für andere Tage übernehmen</h2>
                            <button
                                className={classes.modalCloseButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowModal(false);
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                                </svg>
                            </button>
                        </header>
                        <div className={classes.modalBody}>
                            <div className={classes.modalSection}>
                                <h3 className="semiboldMiddle">Betriebszeit</h3>
                                <div className={classes.timeSlotsDisplay}>
                                    {timeSlots
                                        .filter(slot => slot.start && slot.end)
                                        .map((slot, index) => (
                                            <p key={index} className="mediumLarge">
                                                {slot.start} - {slot.end} Uhr
                                            </p>
                                        ))}
                                </div>
                            </div>
                            <div className={classes.modalDaysList}>
                                {allDays.map(day => {
                                    const isSourceDay = day === dayKey;
                                    const hasSameSlots = hasSameTimeSlots(day);
                                    const isDisabled = isSourceDay || hasSameSlots;

                                    return (
                                        <div
                                            onClick={() => !isDisabled && handleDayToggle(day)}
                                            key={day}
                                            className={`${classes.modalDayItem} ${isDisabled ? classes.modalDayItemDisabled : ''}`}
                                        >
                                            <span className="semiboldMiddle">{dayNamesMap[day]}</span>

                                            {isDisabled ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                    <rect x="0.3" y="0.3" width="23.4" height="23.4" rx="5.7" fill="#B1B6BB" stroke="#B1B6BB" strokeWidth="0.6"/>
                                                    <line x1="6.55579" y1="11.8172" x2="10.1295" y2="15.4678" stroke="white" strokeWidth="1.55556"/>
                                                    <line x1="9.03867" y1="15.4501" x2="17.464" y2="7.4363" stroke="white" strokeWidth="1.55556"/>
                                                </svg>
                                            ) : (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedDays.includes(day)}
                                                    onChange={() => handleDayToggle(day)}
                                                    className={classes.dayCheckbox}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                        </div>

                        <div className={classes.modalFooter}>
                            <button
                                className={`${classes.copyButton} mediumLarge`}
                                onClick={handleCopyTimes}
                                disabled={selectedDays.length === 0}
                            >
                                Zeiten übernehmen
                            </button>
                        </div>
                    </div>
                </div>
            </>,
            document.body
        )}
        </div>
    );
};

export const AvailabilityModal = forwardRef(({
    bookingData,
    studioData,
    studioId,
    onSave,
    createAvailability,
    deleteAvailability,
    minNumberOfHours,
    fromEditor,
    from,
    onError
}, ref)=> {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const saveInProgress = useRef(false);

    useImperativeHandle(ref, () => ({
        handleSave: async () => {
            if (!saveInProgress.current) {
                return handleSave();
            }
        },
        refetch: () => {
            if (typeof onSave === 'function') {
                onSave();
            }
        }
    }));

    const processAvailabilities = (availabilities) => {
        // Start with all days as inactive by default
        const defaultState = {
            monday: { isActive: false, is24Hours: true, timeSlots: [{ start: '', end: '' }] },
            tuesday: { isActive: false, is24Hours: true, timeSlots: [{ start: '', end: '' }] },
            wednesday: { isActive: false, is24Hours: true, timeSlots: [{ start: '', end: '' }] },
            thursday: { isActive: false, is24Hours: true, timeSlots: [{ start: '', end: '' }] },
            friday: { isActive: false, is24Hours: true, timeSlots: [{ start: '', end: '' }] },
            saturday: { isActive: false, is24Hours: true, timeSlots: [{ start: '', end: '' }] },
            sunday: { isActive: false, is24Hours: true, timeSlots: [{ start: '', end: '' }] },
        };

        if (!availabilities || !Array.isArray(availabilities) || availabilities.length === 0) {
            return defaultState;
        }

        // Group availabilities by day of week
        const availabilitiesByDay = {};
        availabilities.forEach(availability => {
            if (!availability) return;

            const dayIndex = availability.dayOfWeek === 7 ? 0 : availability.dayOfWeek; // Normalize Sunday (7 → 0)
            if (!availabilitiesByDay[dayIndex]) {
                availabilitiesByDay[dayIndex] = [];
            }
            availabilitiesByDay[dayIndex].push(availability);
        });

        // Map dayOfWeek numbers to day names
        const dayIndexToName = {
            0: 'sunday',
            1: 'monday',
            2: 'tuesday',
            3: 'wednesday',
            4: 'thursday',
            5: 'friday',
            6: 'saturday'
        };

        // Process each day's availabilities
        Object.entries(availabilitiesByDay).forEach(([dayIndex, dayAvailabilities]) => {
            const dayName = dayIndexToName[dayIndex];
            if (!dayName) {
                return;
            }

            // If we have availabilities for this day, the day is active
            defaultState[dayName].isActive = true;

            // Check if this is a 24-hour day (single availability from 00:00 to 23:59)
            const is24HourDay = dayAvailabilities.length === 1 &&
                               dayAvailabilities[0].startTime === "00:00" &&
                               (dayAvailabilities[0].endTime === "23:59" || dayAvailabilities[0].endTime === "24:00");

            defaultState[dayName].is24Hours = is24HourDay;

            // For non-24-hour days, add the specific time slots
            if (!is24HourDay) {
                const timeSlots = dayAvailabilities.map(availability => ({
                    start: `${availability.startHour.toString().padStart(2, '0')}:${availability.startMinute.toString().padStart(2, '0')}`,
                    end: `${availability.endHour.toString().padStart(2, '0')}:${availability.endMinute.toString().padStart(2, '0')}`
                }));

                // Sort time slots by start time
                timeSlots.sort((a, b) => a.start.localeCompare(b.start));

                defaultState[dayName].timeSlots = timeSlots.length > 0 ? timeSlots : [{ start: '', end: '' }];
            } else {
                // For 24-hour days, set a default slot that won't be shown (since is24Hours is true)
                defaultState[dayName].timeSlots = [{ start: '00:00', end: '23:59' }];
            }
        });

        return defaultState;
    };

    const [daysData, setDaysData] = useState(() => processAvailabilities(studioData));

    useEffect(() => {
        if (studioData) {
            setDaysData(processAvailabilities(studioData));
        }
    }, [studioData, studioId, fromEditor, bookingData]);

    const handleDayDataChange = (day, newData) => {
        setDaysData(prev => ({
            ...prev,
            [day]: newData
        }));
    };

    const handleCopyToOtherDays = (sourceDay, targetDays, sourceData) => {
        const newDaysData = { ...daysData };

        targetDays.forEach(targetDay => {
            newDaysData[targetDay] = { ...sourceData };
        });

        setDaysData(newDaysData);
    };

    // Function to get day of week from a date string in format DD.MM.YYYY
    const getDayOfWeek = (dateString) => {
        try {
            // Split date string in format DD.MM.YYYY
            const parts = dateString.split('.');
            if (parts.length !== 3) return null;

            const day = parseInt(parts[0]);
            const month = parseInt(parts[1]) - 1; // JS months are 0-indexed
            const year = parseInt(parts[2]);

            const date = new Date(year, month, day);
            if (isNaN(date.getTime())) return null;

            return date.getDay();
        } catch (e) {
            console.error("Error parsing date:", e);
            return null;
        }
    };

    // Group bookings by day of week for easy access
    const getBookingsByDay = () => {
        if (!bookingData || !Array.isArray(bookingData)) return {};

        // Initialize empty arrays for each day of the week
        const bookingsByDay = {
            0: [], // Sunday
            1: [], // Monday
            2: [], // Tuesday
            3: [], // Wednesday
            4: [], // Thursday
            5: [], // Friday
            6: [], // Saturday
        };

        // Get today's date to filter out past bookings
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to beginning of the day for accurate comparison

        // Process each booking - filter out CANCELLED bookings and past dates
        bookingData
            .filter(booking => {
                // Keep only CONFIRMED or PENDING bookings
                const isActiveBooking = isBusyBookingStatus(booking.status);

                // Convert booking date (format: DD.MM.YYYY) to Date object for comparison
                if (!booking.date) return false;

                const [day, month, year] = booking.date.split('.').map(Number);
                // Note: month is 0-indexed in JS Date
                const bookingDate = new Date(year, month - 1, day);

                // Keep only bookings on or after today
                const isFutureBooking = bookingDate >= today;

                return isActiveBooking && isFutureBooking;
            })
            .forEach(booking => {
                if (!booking.date || !booking.startTime || !booking.endTime) return;

                // Get day of week from date string
                const dayOfWeek = getDayOfWeek(booking.date);
                if (dayOfWeek === null) return; // Skip if invalid date

                // Add booking to the corresponding day
                bookingsByDay[dayOfWeek].push(booking);
            });

        return bookingsByDay;
    };

    const bookingsByDay = getBookingsByDay();

    // Map day names to their corresponding indices
    const dayNameToIndex = {
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
        sunday: 7
    };


    const prepareAvailabilitiesForSave = () => {
        const dayNameToIndex = {
            monday: 1,
            tuesday: 2,
            wednesday: 3,
            thursday: 4,
            friday: 5,
            saturday: 6,
            sunday: 7
        };

        const availabilities = [];

        Object.entries(daysData).forEach(([day, data]) => {
            const dayIndex = dayNameToIndex[day];

            // Only process active days
            if (data.isActive) {
                if (data.is24Hours) {
                    // For 24-hour availability, create a single entry from 00:00 to 23:59
                    availabilities.push({
                        dayOfWeek: dayIndex,
                        startTime: "00:00",
                        endTime: "23:59",
                        startHour: 0,
                        startMinute: 0,
                        endHour: 23,
                        endMinute: 59
                    });
                } else {
                    // For specific time slots
                    data.timeSlots.forEach(slot => {
                        if (slot.start && slot.end) {
                            const [startHour, startMinute] = slot.start.split(':').map(Number);
                            const [endHour, endMinute] = slot.end.split(':').map(Number);

                            availabilities.push({
                                dayOfWeek: dayIndex,
                                startTime: slot.start,
                                endTime: slot.end,
                                startHour,
                                startMinute,
                                endHour,
                                endMinute
                            });
                        }
                    });
                }
            }
        });

        return availabilities;
    };

    const handleSave = async () => {
        if (saveInProgress.current) {
            return;
        }

        try {
            saveInProgress.current = true;
            setIsLoading(true);
            setError(null);

            const newAvailabilities = prepareAvailabilitiesForSave();

            // Fix 2: In the checkBookingConflictsWithDetails function
            const { hasConflicts, affectedDays } = checkBookingConflictsWithDetails(newAvailabilities);

            if (hasConflicts) {
                const affectedDayNames = affectedDays.map(dayIndex => {
                    const dayNames = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
                    return dayNames[dayIndex];
                });
                const errorMsg = `Es gibt Buchungen an folgenden Tagen: ${affectedDayNames.join(", ")}. Bitte stornieren Sie diese Buchungen zuerst.`;
                setError(errorMsg);
                // Notify parent of the error
                if (onError && typeof onError === 'function') {
                    onError(errorMsg);
                }
                return false;
            }

            // Validate time slots
            for (const dayData of Object.values(daysData)) {
                if (dayData.isActive && !dayData.is24Hours) {
                    for (const slot of dayData.timeSlots) {
                        if (slot.start && slot.end) {
                            const startHour = parseInt(slot.start.split(':')[0]);
                            const endHour = parseInt(slot.end.split(':')[0]);
                            if (endHour - startHour < minNumberOfHours) {
                                const errorMsg = `Jeder Zeitslot muss mindestens ${minNumberOfHours} Stunden betragen.`;
                                setError(errorMsg);
                                // Notify parent of the error
                                if (onError && typeof onError === 'function') {
                                    onError(errorMsg);
                                }
                                return false;
                            }
                        }
                    }
                }
            }

            await saveAvailabilityReplacement({
                existingAvailabilities: studioData,
                desiredAvailabilities: newAvailabilities,
                studioId,
                createAvailability,
                deleteAvailability,
            });

            // Only call onSave once after all operations are complete
            if (typeof onSave === 'function') {
                onSave();
            }

            // Clear any errors in parent
            if (onError && typeof onError === 'function') {
                onError(null);
            }

            return true;
        } catch (err) {
            const errorMsg = err.message || "Fehler beim Speichern der Verfugbarkeiten";
            setError(errorMsg);

            // Notify parent of the error
            if (onError && typeof onError === 'function') {
                onError(errorMsg);
            }

            return false;
        } finally {
            setIsLoading(false);
            saveInProgress.current = false;
        }
    };

    // Fix 2: In the checkBookingConflictsWithDetails function
    const checkBookingConflictsWithDetails = (availabilities) => {

        if (!bookingData || !Array.isArray(bookingData) || bookingData.length === 0) {
            return { hasConflicts: false, affectedDays: [] };
        }

        const affectedDays = [];

        // Group availabilities by day
        const availabilitiesByDay = {};
        for (const availability of availabilities) {
            const dayIndex = availability.dayOfWeek === 7 ? 0 : availability.dayOfWeek;
            if (!availabilitiesByDay[dayIndex]) {
                availabilitiesByDay[dayIndex] = [];
            }
            availabilitiesByDay[dayIndex].push(availability);
        }

        // For each day with bookings, check if all bookings are covered
        Object.entries(bookingsByDay).forEach(([dayIndex, dayBookings]) => {
            if (!dayBookings || dayBookings.length === 0) {
                return; // Skip days with no bookings
            }

            dayIndex = parseInt(dayIndex);

            // Get availabilities for this day
            const dayAvailabilities = availabilitiesByDay[dayIndex] || [];

            // If no availabilities but we have bookings, that's a conflict
            if (dayAvailabilities.length === 0) {
                affectedDays.push(dayIndex);
                return;
            }

            // Check if any availability is 24-hours
            const has24HourAvailability = dayAvailabilities.some(avail =>
                avail.startHour === 0 && avail.startMinute === 0 &&
                ((avail.endHour === 23 && avail.endMinute === 59) ||
                 (avail.endHour === 24 && avail.endMinute === 0))
            );

            if (has24HourAvailability) {
                return; // 24-hour availability covers all bookings
            }

            // Sort availabilities by start time
            dayAvailabilities.sort((a, b) => a.startHour - b.startHour);

            // Check for contiguous coverage
            let isContiguous = true;
            for (let i = 0; i < dayAvailabilities.length - 1; i++) {
                if (dayAvailabilities[i].endHour !== dayAvailabilities[i+1].startHour) {
                    isContiguous = false;
                    break;
                }
            }

            // For each booking, check if covered
            for (const booking of dayBookings) {
                const bookingStart = parseInt(booking.startTime.split(':')[0]);
                const bookingEnd = parseInt(booking.endTime.split(':')[0]);

                let isBookingCovered = false;

                if (isContiguous) {
                    // If availabilities are contiguous, check if the combined range covers the booking
                    const startTime = dayAvailabilities[0].startHour;
                    const endTime = dayAvailabilities[dayAvailabilities.length - 1].endHour;

                    isBookingCovered = startTime <= bookingStart && endTime >= bookingEnd;
                } else {
                    // Check if any single availability covers the booking
                    isBookingCovered = dayAvailabilities.some(avail => {
                        const covers = avail.startHour <= bookingStart && avail.endHour >= bookingEnd;
                        return covers;
                    });
                }

                if (!isBookingCovered) {
                    affectedDays.push(dayIndex);
                    break;
                }
            }
        });

        const result = {
            hasConflicts: affectedDays.length > 0,
            affectedDays
        };

        return result;
    };

    // Map day names for display
    const dayNamesGerman = {
        monday: "Montag",
        tuesday: "Dienstag",
        wednesday: "Mittwoch",
        thursday: "Donnerstag",
        friday: "Freitag",
        saturday: "Samstag",
        sunday: "Sonntag"
    };

    return (
        <div className={`${classes.container} ${from === 'calendarPage' ? classes.calendarPageContainer : ''}`}>
            <div className={`${classes.content} ${fromEditor ? classes.fromEditorContent : ''}`}>
                {Object.entries(daysData).map(([day, data], index) => {
                    // Get the correct day index from our mapping
                    const dayIndex = dayNameToIndex[day];

                    return (
                        <div className={classes.contentContainer} key={day}>
                            {index > 0 && <div className={classes.horrizontalLine}></div>}
                            <DayComponent
                                dayName={dayNamesGerman[day]}
                                dayData={data}
                                onDayDataChange={(newData) => handleDayDataChange(day, newData)}
                                minNumberOfHours={minNumberOfHours}
                                bookings={bookingsByDay[dayIndex]}
                                dayIndex={dayIndex}
                                dayKey={day}
                                allDaysData={daysData}
                                onCopyToOtherDays={handleCopyToOtherDays}
                            />
                        </div>
                    );
                })}
            </div>
            {error &&
                <div className={classes.errorMessage}>
                    <ErrorMessage >{error}</ErrorMessage>
                </div>
            }
            {!fromEditor &&
                <footer className={`${classes.blockTimeModalFooter} ${from === 'calendarPage' ? classes.calendarPageFooter : ''}`}>
                    <button
                        className="mediumLarge"
                        onClick={handleSave}
                        disabled={isLoading}
                    >
                        {isLoading ? "Wird gespeichert..." : "Speichern"}
                    </button>
                </footer>
            }
        </div>
    );
});

export default AvailabilityModal;
